import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import confetti from "canvas-confetti";
import { useAuth } from "../contexts/AuthContext";
import VinylWheel from "../components/VinylWheel";
import { getWheelConfig, pickWinnerIndex, recordSpin, listUserSpins } from "../services/wheel";
import { listOrdersByUser } from "../services/analytics";
import { Lock, ShoppingBag, Copy, Check, Gift, Truck, Percent, XCircle, Sparkles, X } from "lucide-react";

const PRIZE_META = {
  coupon_percent: { icon: Percent, color: "#c53a24", tag: "ENDİRİM" },
  free_shipping:  { icon: Truck,   color: "#2e7ec7", tag: "ÇATDIRILMA" },
  gift_item:      { icon: Gift,    color: "#c98b4b", tag: "HƏDİYYƏ" },
  empty:          { icon: XCircle, color: "#7a7a7a", tag: "—" },
};

export default function WheelPage() {
  const { user, authChecked } = useAuth();
  const [params] = useSearchParams();
  const forcedOrderId = params.get("order_id") || "";
  const isDemo = params.get("demo") === "1";

  const [cfg, setCfg] = useState(null);
  const [pastSpins, setPastSpins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [targetOrder, setTargetOrder] = useState(null);
  const [existingSpin, setExistingSpin] = useState(null);

  const [spinning, setSpinning] = useState(false);
  const [winnerIdx, setWinnerIdx] = useState(-1);
  const [result, setResult] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [wheelSize, setWheelSize] = useState(380);
  const confettiFired = useRef(false);

  // Responsive wheel size
  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      if (w < 380) setWheelSize(260);
      else if (w < 640) setWheelSize(300);
      else if (w < 1024) setWheelSize(340);
      else setWheelSize(400);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  useEffect(() => {
    if (!authChecked) return;
    (async () => {
      const c = await getWheelConfig();
      setCfg(c);
      if (!user) { setLoading(false); return; }
      const [ords, spins] = await Promise.all([
        listOrdersByUser(user.uid),
        listUserSpins(user.uid),
      ]);
      const spinsByOrder = new Map(spins.map((s) => [s.order_id, s]));
      setPastSpins(spins);

      const min = Number(c.min_order_azn) || 0;
      const qualifying = ords
        .filter((o) => Number(o.total_azn || 0) >= min)
        .filter((o) => !spinsByOrder.has(o.id))
        .sort((a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0));

      let target = null;
      if (forcedOrderId) {
        target = ords.find((o) => o.id === forcedOrderId) || null;
        if (target && spinsByOrder.has(target.id)) {
          setExistingSpin(spinsByOrder.get(target.id));
          target = null;
        }
      }
      if (!target && qualifying.length) target = qualifying[0];
      setTargetOrder(target);
      setLoading(false);
    })();
  }, [authChecked, user, forcedOrderId]);

  const slices = useMemo(() => cfg?.slices || [], [cfg]);

  const onSpinClick = () => {
    if ((!targetOrder && !isDemo) || !cfg || !slices.length || spinning) return;
    const idx = pickWinnerIndex(slices);
    setWinnerIdx(idx);
    setSpinning(true);
    setResult(null);
    confettiFired.current = false;
  };

  const onSpinEnd = async () => {
    const slice = slices[winnerIdx];
    setResult(slice);
    setSpinning(false);
    setShowModal(true);
    // Confetti (only for real prizes)
    if (slice.type !== "empty" && !confettiFired.current) {
      confettiFired.current = true;
      fireConfetti();
    }
    if (isDemo) return;
    try {
      await recordSpin(targetOrder.id, user?.uid, slice);
    } catch (e) {
      console.error("recordSpin failed", e);
    }
  };

  const couponCode = result && result.type === "coupon_percent" && result.percent > 0
    ? `WHEEL${result.percent}-${(targetOrder?.tracking_no || targetOrder?.id || "DEMO").slice(-6).toUpperCase()}`
    : result && result.type === "free_shipping"
    ? `SHIP-${(targetOrder?.tracking_no || targetOrder?.id || "DEMO").slice(-6).toUpperCase()}`
    : "";

  const copyCoupon = () => {
    if (!couponCode) return;
    navigator.clipboard.writeText(couponCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (!authChecked || loading) {
    return <div className="min-h-[70vh] flex items-center justify-center text-[var(--text-muted)]">Yüklənir…</div>;
  }

  if (!user && !isDemo) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
        <div className="max-w-md text-center">
          <Lock size={32} className="text-[var(--accent)] mx-auto mb-6" />
          <div className="text-[11px] tracking-[0.24em] text-[var(--accent)] mb-3">LUCKY WHEEL</div>
          <h1 className="font-display text-4xl md:text-5xl mb-4">Şans Çarxı</h1>
          <p className="text-[var(--text-2)] mb-6 text-sm">Çarxı fırlatmaq üçün hesabınıza daxil olun.</p>
          <Link to="/login?next=/wheel" className="btn-primary" style={{ textTransform: "none", letterSpacing: "0" }}>
            Daxil ol
          </Link>
        </div>
      </div>
    );
  }

  if (!cfg?.enabled) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
        <div className="max-w-md text-center">
          <h1 className="font-display text-3xl mb-3">Şans Çarxı hələlik söndürülüb</h1>
          <p className="text-[var(--text-muted)]">Bir az sonra yenidən yoxlayın.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[85vh] bg-[var(--bg)] px-4 sm:px-6 md:px-10 py-14 md:py-20">
      <div className="max-w-5xl mx-auto">
        {/* Hero */}
        <div className="text-center mb-14 md:mb-20">
          <div className="text-[11px] tracking-[0.28em] text-[var(--accent)] mb-4">LUCKY WHEEL</div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl mb-4 leading-none">Şans Çarxı</h1>
          <div className="text-[13px] text-[var(--text-muted)] max-w-md mx-auto">
            Sifariş üzərindən 1 dəfə fırladın · Min. {cfg.min_order_azn} AZN
          </div>
        </div>

        {/* Wheel */}
        <div className="flex justify-center mb-12 md:mb-16">
          <VinylWheel
            slices={slices}
            spinning={spinning}
            targetIndex={winnerIdx}
            onSpinEnd={onSpinEnd}
            onSpinClick={onSpinClick}
            disabled={(!targetOrder && !isDemo) || !!result}
            size={wheelSize}
          />
        </div>

        {/* Status line */}
        <div className="text-center mb-14 max-w-lg mx-auto">
          {existingSpin ? (
            <div className="text-[13px] text-[var(--text-muted)]" data-testid="wheel-already">
              Bu sifariş üzərində artıq fırlatmısınız · Qazandığınız: <span className="text-[var(--accent)]">{existingSpin.slice_label}</span>
            </div>
          ) : !targetOrder && !isDemo ? (
            <div className="space-y-3" data-testid="wheel-noorder">
              <div className="flex items-center justify-center gap-2 text-[var(--text-muted)] text-sm">
                <ShoppingBag size={14} /> Uyğun sifariş tapılmadı
              </div>
              <div className="text-[13px] text-[var(--text-muted)]">
                Fırlatmaq üçün min. {cfg.min_order_azn} AZN dəyərində sifariş verin.
              </div>
              <Link to="/catalog" className="inline-block text-sm text-[var(--accent)] hover:underline">
                Kataloqa keç →
              </Link>
            </div>
          ) : targetOrder ? (
            <div className="text-[12px] text-[var(--text-muted)]" data-testid="wheel-ready">
              Sifariş #{targetOrder.tracking_no || targetOrder.id?.slice(0, 8)} · {Number(targetOrder.total_azn || 0).toFixed(2)} AZN
            </div>
          ) : (
            <div className="text-[12px] text-[var(--text-muted)]">Demo rejim — nəticələr yaddaşa alınmır</div>
          )}
        </div>

        {/* Prize cards — improved design */}
        <div className="border-t border-[var(--border)] pt-12 md:pt-16">
          <div className="text-center mb-8">
            <div className="text-[10px] tracking-[0.28em] text-[var(--text-muted)] mb-2">HƏDİYYƏLƏR</div>
            <h2 className="font-display text-2xl md:text-3xl">Nə Qazana Bilərsiniz?</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 max-w-4xl mx-auto">
            {slices.map((s, i) => {
              const meta = PRIZE_META[s.type] || PRIZE_META.empty;
              const Icon = meta.icon;
              const isWon = result && winnerIdx === i;
              return (
                <div
                  key={i}
                  className={`relative group border transition-all p-4 md:p-5 flex flex-col items-center text-center bg-[var(--surface)] ${
                    isWon
                      ? "border-[var(--accent)] shadow-lg scale-[1.02]"
                      : "border-[var(--border)] hover:border-[var(--text-2)]"
                  }`}
                  data-testid={`wheel-prize-${i}`}
                >
                  {/* Number badge */}
                  <div
                    className={`absolute -top-3 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full flex items-center justify-center font-display text-sm border-2 ${
                      isWon ? "bg-[var(--accent)] text-white border-[var(--accent)]" : "bg-[#1a1a1a] text-[#e8d7b8] border-[var(--surface)]"
                    }`}
                  >
                    {s.number}
                  </div>
                  {/* Icon */}
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center mb-3 mt-2"
                    style={{ background: `${meta.color}22`, color: meta.color }}
                  >
                    <Icon size={20} />
                  </div>
                  {/* Tag */}
                  <div className="text-[9px] tracking-[0.18em] font-medium mb-1" style={{ color: meta.color }}>
                    {meta.tag}
                  </div>
                  {/* Label */}
                  <div className="text-sm font-medium leading-tight" title={s.label}>
                    {s.label}
                  </div>
                  {/* Sub info */}
                  {s.type === "coupon_percent" && s.percent > 0 && (
                    <div className="text-[10px] text-[var(--text-muted)] mt-1">Endirim kuponu</div>
                  )}
                  {isWon && (
                    <div className="absolute inset-0 border-2 border-[var(--accent)] pointer-events-none animate-pulse" style={{ animationDuration: "1.6s" }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Past spins footer */}
        {pastSpins.length > 0 && (
          <div className="mt-16 pt-8 border-t border-[var(--border)]/60 text-center">
            <div className="text-[10px] tracking-[0.28em] text-[var(--text-muted)] mb-3">KEÇMİŞ FIRLANMALAR</div>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-1 text-[12px]">
              {pastSpins.slice(0, 6).map((s) => (
                <div key={s.id} className="text-[var(--text-muted)]">
                  #{s.order_id.slice(0, 6)} · <span className="text-[var(--text)]">{s.slice_label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Winner modal */}
      {showModal && result && (
        <WinnerModal
          result={result}
          couponCode={couponCode}
          copied={copied}
          onCopy={copyCoupon}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}

function fireConfetti() {
  const colors = ["#c53a24", "#e2c7a5", "#f4dfb6", "#1a1a1a", "#d84a2f"];
  const end = Date.now() + 2200;
  (function frame() {
    confetti({
      particleCount: 4,
      angle: 60,
      spread: 65,
      origin: { x: 0, y: 0.6 },
      colors,
      scalar: 0.9,
    });
    confetti({
      particleCount: 4,
      angle: 120,
      spread: 65,
      origin: { x: 1, y: 0.6 },
      colors,
      scalar: 0.9,
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
  // Big burst in the middle
  setTimeout(() => {
    confetti({
      particleCount: 120,
      spread: 90,
      startVelocity: 45,
      origin: { x: 0.5, y: 0.5 },
      colors,
    });
  }, 200);
}

function WinnerModal({ result, couponCode, copied, onCopy, onClose }) {
  const meta = PRIZE_META[result.type] || PRIZE_META.empty;
  const Icon = meta.icon;
  const isEmpty = result.type === "empty";

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="wheel-winner-modal">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-in fade-in duration-300"
        onClick={onClose}
      />
      {/* Modal card */}
      <div
        className="relative w-full max-w-md bg-[var(--surface)] border border-[var(--border)] shadow-2xl overflow-hidden"
        style={{ animation: "wm-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both" }}
      >
        <button onClick={onClose} className="absolute top-3 right-3 text-[var(--text-muted)] hover:text-[var(--text)] z-10">
          <X size={18} />
        </button>

        {/* Big colour header */}
        <div
          className="p-8 text-center relative overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${meta.color} 0%, ${adjustColor(meta.color, -20)} 100%)` }}
        >
          {/* Decorative sparkles */}
          <Sparkles size={16} className="absolute top-4 left-6 text-white/40" />
          <Sparkles size={12} className="absolute top-12 right-10 text-white/30" />
          <Sparkles size={14} className="absolute bottom-4 left-12 text-white/30" />

          <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur flex items-center justify-center mx-auto mb-4 border-2 border-white/40">
            <Icon size={38} className="text-white" />
          </div>
          <div className="text-[10px] tracking-[0.28em] text-white/80 mb-2">{meta.tag}</div>
          <div className="text-white font-display text-3xl md:text-4xl leading-tight">
            {isEmpty ? "Növbəti dəfə!" : "Təbriklər!"}
          </div>
        </div>

        {/* Body */}
        <div className="p-6 text-center space-y-4">
          <div className="font-display text-2xl">{result.label}</div>

          {result.type === "coupon_percent" && result.percent > 0 && (
            <p className="text-sm text-[var(--text-muted)]">
              Növbəti sifarişdə <span className="text-[var(--accent)] font-medium">{result.percent}%</span> endirim
              qazandınız. Aşağıdakı kodu istifadə edin:
            </p>
          )}
          {result.type === "free_shipping" && (
            <p className="text-sm text-[var(--text-muted)]">
              Növbəti sifarişinizin çatdırılması bizdəndir! Aşağıdakı kodu istifadə edin:
            </p>
          )}
          {result.type === "gift_item" && (
            <p className="text-sm text-[var(--text-muted)]">
              Hədiyyəniz növbəti sifarişinizlə birlikdə pulsuz göndəriləcək. Komandamız sizinlə əlaqə saxlayacaq.
            </p>
          )}
          {isEmpty && (
            <p className="text-sm text-[var(--text-muted)]">
              Bu dəfə şansınız gətirmədi — amma yeni sifariş vermək daha çox şans deməkdir!
            </p>
          )}

          {couponCode && (
            <div className="flex items-center justify-center gap-2 pt-2">
              <code className="px-4 py-3 border-2 border-dashed border-[var(--accent)] bg-[var(--bg)] font-mono text-base tracking-widest">
                {couponCode}
              </code>
              <button onClick={onCopy} className="p-3 border border-[var(--border)] hover:border-[var(--accent)]" data-testid="wheel-copy-code">
                {copied ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
              </button>
            </div>
          )}

          <button onClick={onClose} className="btn-primary w-full justify-center mt-4" style={{ textTransform: "none", letterSpacing: "0" }}>
            Bağla
          </button>
        </div>
      </div>

      <style>{`
        @keyframes wm-pop {
          0% { transform: scale(0.7) translateY(20px); opacity: 0; }
          100% { transform: scale(1) translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// Simple color darkening helper
function adjustColor(hex, amount) {
  const num = parseInt(hex.replace("#", ""), 16);
  const r = Math.max(0, Math.min(255, (num >> 16) + amount));
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + amount));
  const b = Math.max(0, Math.min(255, (num & 0xff) + amount));
  return "#" + ((r << 16) | (g << 8) | b).toString(16).padStart(6, "0");
}
